// Client-safe helpers for building responsive srcset from a variants map.
// A variants map is: { "400": "<url>", "800": "<url>", ... }.
// When the map is null/empty we fall back to today's single-URL behavior.

export type VariantMap = Record<string, string> | null | undefined;

const CANONICAL_WIDTHS = [400, 800, 1200, 1600, 2400] as const;

function normalize(variants: VariantMap): [number, string][] {
  if (!variants || typeof variants !== "object") return [];
  const entries: [number, string][] = [];
  for (const [k, v] of Object.entries(variants)) {
    const w = Number(k);
    if (Number.isFinite(w) && w > 0 && typeof v === "string" && v.length > 0) {
      entries.push([w, v]);
    }
  }
  entries.sort((a, b) => a[0] - b[0]);
  return entries;
}

export function buildSrcSet(variants: VariantMap): string | undefined {
  const entries = normalize(variants);
  if (entries.length === 0) return undefined;
  return entries.map(([w, url]) => `${url} ${w}w`).join(", ");
}

// Pick a mid-range variant for the `src` attribute (used as a fallback for
// browsers that ignore srcset). Prefer 800 if present, otherwise the largest
// available, otherwise the caller-provided fallback.
export function pickSrc(variants: VariantMap, fallback: string): string {
  const entries = normalize(variants);
  if (entries.length === 0) return fallback;
  const eightHundred = entries.find(([w]) => w === 800);
  if (eightHundred) return eightHundred[1];
  return entries[entries.length - 1][1];
}

export function hasVariants(variants: VariantMap): boolean {
  return normalize(variants).length > 0;
}

export { CANONICAL_WIDTHS };
